import { Button } from '@/components/ui/buttons';
import ShippingInfoBranchCard from '@/components/ui/cards/shipping-info-branch-card';
import ArrowIcon from '@/components/ui/icons/arrow-icon';
import HomeDeliveryIcon from '@/components/ui/icons/home-delivery-icon';
import ShippingInfoBranchCardSkeleton from '@/components/ui/skeletons/Shipping-Info-Branch-Card-Skeleton';
import { SHIPPING_INFO_MODALS_NAMES } from '@/constants';
import { useBranches } from '@/hooks/useBranches';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { useSession } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { event_button_click } from '@/utils/gtm';

const NearestBranches = ({
  handleOnClose,
  setSelectedBranch,
  selectedBranch,
  setModalStep,
  setShippingMethod,
}) => {
  const t = useTranslations('modals');
  const [show, setShow] = useState(false);
  const chooseCityRef = useRef();

  const [selectedBranchData, setSelectedBranchData] = useState(selectedBranch);
  const { data: session } = useSession();

  const {
    cities,
    query,
    selectedCity,
    isLoading,
    branchesToDisplay,
    handleChangeCity,
    handleSearch,
  } = useBranches();

  useOnClickOutside([chooseCityRef], () => show && setShow(false));

  const handleCitySelect = (city, idx) => {
    handleChangeCity(city, idx);
    setShow(false);
  };

  const handleBranchSelect = (branch) => {
    const selectedBranch = {
      firstname: branch?.name,
      street: [branch?.address],
      telephone: branch?.phone || session?.user?.mobilenumber,
      region_id: branch?.source_region_id,
      country_id: branch?.country,
      id: branch?.id,
      postcode: branch?.zip,
      lat: branch?.lat,
      lng: branch?.lng,
      city: branch?.city,
      email: session?.user?.email,
      city_id: branch?.source_city_id,
    };
    setSelectedBranchData(selectedBranch);
  };

  return (
    <div className='space-y-4 px-[22px] pt-3.5 pb-[19px]'>
      <div className='border-b border-b-gray-200 pb-2'>
        <h2 className='text-lg font-semibold'>{t('choose_branch')}</h2>
        <p className='text-xs text-gray-500'>{t('choose_branch_desc')}</p>
      </div>

      <div className='flex items-center justify-between'>
        <div
          ref={chooseCityRef}
          onClick={() => setShow(!show)}
          className='relative grid w-62 cursor-pointer items-center justify-between rounded-[10px] border border-[#E4E4F9] bg-white py-2 ps-3 pe-4 max-lg:grid-cols-2 max-lg:py-1.5 max-sm:h-8.75 max-sm:w-40 lg:flex lg:max-h-[45px] lg:min-h-[45px]'
        >
          <div>
            <div
              onClick={() => setShow((prev) => !prev)}
              className='text-opacity-60 flex items-center gap-2 text-black'
            >
              <span className='text-sm max-lg:text-xs'>
                {cities[selectedCity] || t('Select City')}
              </span>
            </div>
            {show && (
              <div className='absolute top-12 left-0 z-50 max-h-98 w-full overflow-hidden overflow-y-auto rounded-[10px] bg-white shadow-2xs max-sm:top-10 lg:w-62'>
                {cities?.map((city, idx) => (
                  <div
                    onClick={() => handleCitySelect(city, idx)}
                    key={city + '-' + idx}
                    className='p-2 text-xs hover:bg-slate-100 md:text-base'
                  >
                    {city}
                  </div>
                ))}
              </div>
            )}
          </div>
          <ArrowIcon className='size-2.5 max-lg:ms-auto max-lg:size-2' />
        </div>
      </div>

      <div className='relative'>
        <input
          value={query}
          onChange={handleSearch}
          placeholder={t('searchPlaceholder')}
          className='focus:ring-primary w-full rounded-xl border px-9 py-3 text-sm outline-none focus:ring-2 max-sm:px-4 max-sm:placeholder:text-xs'
        />
        <span className='pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500'>
          <svg
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            className='h-5 w-5'
          >
            <circle cx='11' cy='11' r='7' />
            <path d='M20 20l-3.5-3.5' />
          </svg>
        </span>
      </div>

      {isLoading ? (
        Array.from({ length: 3 }).map((_, i) => (
          <ShippingInfoBranchCardSkeleton key={'branch-skeleton-' + i} />
        ))
      ) : (
        <div className='blue-scroll max-h-[35vh] overflow-y-auto px-1 max-sm:px-0'>
          {branchesToDisplay?.length > 0 ? (
            branchesToDisplay?.map((branch, i) => (
              <ShippingInfoBranchCard
                key={'branch-card-' + i}
                branch={{ ...branch, available: true }}
                t={t}
                handleBranchSelect={handleBranchSelect}
                selectedBranch={selectedBranchData}
              />
            ))
          ) : (
            <div className='py-8 text-center text-gray-500'>
              {t('no_branches_found') || 'No branches found'}
            </div>
          )}
        </div>
      )}

      <AddressOption
        icon={HomeDeliveryIcon}
        label={t('Switch to Home Delivery')}
        onClick={() => {
          event_button_click({
            button_name: 'Switch to Home Delivery',
            button_destination: 'switch_to_home_delivery',
            button_location: 'shipping_modal',
          });
          setModalStep(SHIPPING_INFO_MODALS_NAMES.DELIVERY_ADDRESS);
          setShippingMethod(SHIPPING_INFO_MODALS_NAMES.HOME_DELIVERY);
        }}
      />

      <div className='border-t border-t-[#DDDDDD] max-sm:px-1.75'>
        <Button
          disabled={!selectedBranchData?.id}
          onClick={() => {
            if (selectedBranchData?.id) {
              event_button_click({
                button_name: 'Confirm Branch',
                button_destination: 'confirm_branch_selection',
                button_location: 'shipping_modal',
              });
              setSelectedBranch(selectedBranchData);
              handleOnClose();
            }
          }}
          className='bg-primary mx-auto mt-[15px] flex h-[45px] w-[36.688rem] items-center justify-center rounded-[5px] text-[15px] font-bold text-white max-sm:w-full'
        >
          {t('confirm_branch')}
        </Button>
      </div>
    </div>
  );
};

export default NearestBranches;

function AddressOption({ icon: Icon, label, onClick }) {
  return (
    <div
      onClick={onClick}
      role='button'
      tabIndex={0}
      className={`hover:border-primary flex h-[5.313rem] cursor-pointer items-center justify-between rounded-[10px] border border-gray-200 px-[19px] transition-all duration-150 max-sm:h-16`}
    >
      <div className='flex h-full items-center gap-[16px]'>
        <div className='flex h-12 w-12 items-center justify-center rounded-full bg-[#F2F3F5]'>
          <Icon className='h-[27px] w-[27px] text-gray-700' />
        </div>
        <h3 className='mb-[2px] text-[16px] font-bold text-gray-900 max-sm:text-sm'>
          {label}
        </h3>
      </div>
    </div>
  );
}
